import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  getRunOfShow, updateRunOfShow,
  getCrewSlots, updateCrewSlots, confirmCrewSlot,
  getGuestList, updateGuestList,
} from '../lib/api';
import {
  ChevronDown, Plus, Check, Clock,
  Users, ListChecks, ClipboardList, UserPlus, Trash2, Play, Pause,
} from 'lucide-react';
import { toast } from 'sonner';
import { getReferenceTime } from '../utils/gigTime';

/* ─── Shared ─── */
const AMBER = '#C8923E';
const DIM_BG = '#2a2e2b';
const PANEL_BG = '#161a17';
const MONO = "'DM Mono', monospace";
/* Panel headings render at 18px — under the 1.375rem display floor — so they take
   the mono body face at 500 rather than the display serif. */
const DISPLAY = 'var(--s8-font-mono)';
const BODY = "'Barlow', sans-serif";

function PanelShell({ icon: Icon, title, testId, summary, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testId}
      className="mb-6"
      style={{ background: PANEL_BG, borderRadius: 12, border: '1px solid rgba(212, 169, 90, 0.12)' }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4"
        data-testid={`${testId}-toggle`}
        type="button"
      >
        <div className="flex flex-col items-start">
          <span className="flex items-center gap-2.5">
            <Icon size={18} color={AMBER} />
            <span style={{ fontFamily: DISPLAY, fontWeight: 500, fontSize: 18, letterSpacing: '0.04em', color: '#F0EBE0' }}>
              {title}
            </span>
          </span>
          {!open && summary && (
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: '#a39889', marginTop: 2, marginLeft: 30 }}>
              {summary}
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          color="#888"
          style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
        />
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

/* ════════════════════ READINESS SIGNAL ════════════════════ */

const NODES = [
  { key: 'posted',     label: 'POSTED' },
  { key: 'crewSet',    label: 'CREW' },
  { key: 'listLocked', label: 'LINEUP' },
  { key: 'showReady',  label: 'READY' },
];

const GREEN = '#22c55e';

export function ReadinessSignal({ booking, crewSlots, guestList, runOfShow }) {
  const isConfirmed = booking?.status === 'BASICS_CONFIRMED';
  const allCrewConfirmed = crewSlots.length > 0 && crewSlots.every(s => s.confirmed);
  const listApproved = guestList?.approved === true;
  const hasRunOfShow = runOfShow.length > 0;

  const showDate = booking?.snapshot?.date || booking?.date;
  const isShowDay = showDate && new Date(showDate).toDateString() === getReferenceTime(booking).toDateString();

  // State: sequential pipeline — only one amber at a time
  // Find the first incomplete step; everything before it = complete (green),
  // that step = inProgress (amber), everything after = notStarted (grey).
  const completed = {
    posted: isConfirmed,
    crewSet: allCrewConfirmed,
    listLocked: listApproved,
    showReady: hasRunOfShow && isShowDay,
  };

  const keys = NODES.map(n => n.key);
  let firstIncomplete = keys.length; // default: all done
  for (let i = 0; i < keys.length; i++) {
    if (!completed[keys[i]]) { firstIncomplete = i; break; }
  }

  const nodeState = {};
  keys.forEach((k, i) => {
    nodeState[k] = i < firstIncomplete ? 'complete' : i === firstIncomplete ? 'inProgress' : 'notStarted';
  });

  return (
    <div
      data-testid="readiness-signal"
      style={{ background: PANEL_BG, borderRadius: 12, border: '1px solid rgba(212, 169, 90, 0.12)' }}
      className="p-4 mb-4"
    >
      <div className="flex items-center justify-center gap-2 sm:gap-3 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', scrollSnapType: 'x mandatory' }}>
        {NODES.map((node, i) => {
          const state = nodeState[node.key];
          const nodeColor = state === 'complete' ? GREEN : state === 'inProgress' ? AMBER : null;
          const prevComplete = i > 0 && nodeState[NODES[i - 1].key] === 'complete';
          const thisComplete = state === 'complete';
          const connectorOn = prevComplete && thisComplete;
          return (
            <React.Fragment key={node.key}>
              {i > 0 && (
                <div
                  className="hidden sm:block flex-shrink-0"
                  style={{
                    width: 24, height: 2,
                    background: connectorOn ? GREEN : '#3a3e3b',
                  }}
                />
              )}
              <div
                data-testid={`readiness-${node.key}`}
                className={`flex-shrink-0 flex items-center justify-center px-3 rounded-full ${state === 'inProgress' ? 's8-heartbeat' : ''}`}
                style={{
                  background: nodeColor ? `${nodeColor}18` : DIM_BG,
                  border: `1px solid ${nodeColor || '#3a3e3b'}`,
                  scrollSnapAlign: 'center',
                  height: 28,
                  ...(state === 'inProgress' ? { '--hb-color': `${AMBER}66` } : {}),
                }}
              >
                <span
                  style={{
                    fontFamily: MONO,
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: 0.5,
                    color: nodeColor || '#666',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}
                >
                  {node.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/* ════════════════════ RUN OF SHOW ════════════════════ */

export function RunOfShowPanel({ bookingId, slots, setSlots, summary }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRow, setNewRow] = useState({ label: '', time: '', duration_minutes: '', notes: '' });
  const [expanded, setExpanded] = useState(null);      // slot_id whose notes are open
  const [activeIdx, setActiveIdx] = useState(-1);      // walkthrough highlight position
  const [playing, setPlaying] = useState(false);

  // Walkthrough: step the amber highlight down the schedule, one slot at a time.
  useEffect(() => {
    if (!playing) return undefined;
    if (activeIdx >= slots.length - 1) { setPlaying(false); return undefined; }
    const t = setTimeout(() => setActiveIdx((i) => i + 1), 850);
    return () => clearTimeout(t);
  }, [playing, activeIdx, slots.length]);

  const handlePlay = () => { setActiveIdx(0); setPlaying(true); };
  const handleStop = () => { setPlaying(false); setActiveIdx(-1); };

  const load = useCallback(async () => {
    try {
      const res = await getRunOfShow(bookingId);
      setSlots(res.data);
    } catch { /* empty on first load is fine */ }
    finally { setLoading(false); }
  }, [bookingId, setSlots]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newRow.label || !newRow.time) return;
    const slot = {
      slot_id: `ros-${Date.now()}`,
      label: newRow.label,
      time: newRow.time,
      duration_minutes: parseInt(newRow.duration_minutes) || 0,
      notes: newRow.notes || null,
    };
    const updated = [...slots, slot];
    setSaving(true);
    try {
      await updateRunOfShow(bookingId, updated);
      setSlots(updated);
      setNewRow({ label: '', time: '', duration_minutes: '', notes: '' });
      toast.success('Run of show updated');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleRemove = async (slotId) => {
    const updated = slots.filter(s => s.slot_id !== slotId);
    setSaving(true);
    try {
      await updateRunOfShow(bookingId, updated);
      setSlots(updated);
      toast.success('Slot removed');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <PanelShell icon={ClipboardList} title="RUN OF SHOW" testId="run-of-show-panel" summary={summary}>
      {loading ? (
        <div className="h-16 rounded bg-[#2a2e2b] animate-pulse" />
      ) : (
        <>
          {slots.length === 0 && (
            <p style={{ fontFamily: BODY, fontSize: 13, color: '#666' }} className="mb-3">
              No entries yet. Add the first slot below.
            </p>
          )}

          {slots.length > 0 && (
            <>
              {/* Walkthrough control */}
              <div className="flex items-center justify-between mb-3">
                <span style={{ fontFamily: MONO, fontSize: 11, color: '#888', letterSpacing: 0.5 }}>
                  {slots.length} slots · tap a row for details
                </span>
                <button
                  type="button"
                  onClick={playing ? handleStop : handlePlay}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-colors hover:bg-[rgba(200,146,62,0.1)]"
                  style={{ border: `1px solid ${AMBER}`, color: AMBER, fontFamily: MONO, fontSize: 11, letterSpacing: 0.5 }}
                  data-testid="ros-walkthrough"
                >
                  {playing ? <Pause size={12} /> : <Play size={12} />}
                  {playing ? 'Stop' : 'Walkthrough'}
                </button>
              </div>

              {/* Timeline */}
              <div className="mb-4">
                {slots.map((s, i) => {
                  const isCurfew = s.label === 'Curfew';
                  const hasTBC = s.label.includes('TBC');
                  const labelParts = hasTBC ? s.label.split('TBC') : null;
                  const passed = activeIdx >= 0 && i <= activeIdx;
                  const isActive = i === activeIdx;
                  const isExpanded = expanded === s.slot_id;
                  const hasNotes = !!s.notes;
                  const dotColor = isCurfew ? '#B85040' : AMBER;
                  const isLast = i === slots.length - 1;
                  return (
                    <div
                      key={s.slot_id}
                      data-testid={`ros-slot-${s.slot_id}`}
                      className="ros-row group flex gap-3"
                      style={{ animation: 'ros-row-in 0.4s ease both', animationDelay: `${i * 55}ms`, opacity: isCurfew && !passed ? 0.6 : 1 }}
                    >
                      {/* Time */}
                      <span style={{ fontFamily: MONO, fontSize: 13, color: passed ? AMBER : '#9a8f80', minWidth: 46, textAlign: 'right', paddingTop: 8, transition: 'color 0.3s' }}>
                        {s.time}
                      </span>

                      {/* Rail + dot */}
                      <div className="relative flex flex-col items-center" style={{ width: 14 }}>
                        <span
                          className={isActive ? 'ros-dot-active' : ''}
                          style={{
                            marginTop: 7, width: 12, height: 12, borderRadius: '50%', zIndex: 1, flexShrink: 0,
                            background: passed ? dotColor : 'transparent',
                            border: `2px solid ${passed ? dotColor : (isCurfew ? '#B85040' : '#3a3f3b')}`,
                            transition: 'background 0.3s, border-color 0.3s',
                          }}
                        />
                        {!isLast && (
                          <span style={{ position: 'relative', flex: 1, width: 2, marginTop: 2, marginBottom: -2, background: '#2a2e2b', minHeight: 14 }}>
                            <span style={{ position: 'absolute', inset: 0, background: AMBER, transformOrigin: 'top', transform: `scaleY(${activeIdx > i ? 1 : 0})`, transition: 'transform 0.45s ease' }} />
                          </span>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pb-3">
                        <div
                          onClick={() => hasNotes && setExpanded(isExpanded ? null : s.slot_id)}
                          className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 transition-colors group-hover:bg-white/[0.03]"
                          style={{ cursor: hasNotes ? 'pointer' : 'default', background: isActive ? 'rgba(200,146,62,0.08)' : 'transparent' }}
                          data-testid={`ros-row-${s.slot_id}`}
                        >
                          <span style={{ fontFamily: BODY, fontSize: 14, fontWeight: isActive ? 600 : 400, color: isCurfew ? '#9a8f80' : '#F0EBE0', flex: 1 }}>
                            {hasTBC ? (<>{labelParts[0]}<span style={{ color: AMBER }}>TBC</span>{labelParts[1]}</>) : s.label}
                          </span>
                          {s.duration_minutes > 0 && (
                            <span style={{ fontFamily: MONO, fontSize: 12, color: '#888' }}>{s.duration_minutes}m</span>
                          )}
                          {hasNotes && (
                            <ChevronDown size={14} color="#888" style={{ transition: 'transform 0.25s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }} />
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleRemove(s.slot_id); }}
                            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
                            data-testid={`ros-remove-${s.slot_id}`}
                          >
                            <Trash2 size={13} color="#666" />
                          </button>
                        </div>

                        {/* Expandable notes (grid-rows 0fr→1fr height animation) */}
                        {hasNotes && (
                          <div style={{ display: 'grid', gridTemplateRows: isExpanded ? '1fr' : '0fr', transition: 'grid-template-rows 0.3s ease' }}>
                            <div style={{ overflow: 'hidden' }}>
                              <p style={{ fontFamily: BODY, fontSize: 13, color: '#a39889', padding: '2px 10px 4px', lineHeight: 1.5 }} data-testid={`ros-notes-${s.slot_id}`}>
                                {s.notes}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Add row */}
          {showAddForm ? (
            <div className="flex flex-wrap gap-2 items-end" data-testid="ros-add-form">
              <div className="flex-1 min-w-[120px]">
                <label style={{ fontFamily: MONO, fontSize: 10, color: '#666', letterSpacing: 0.5 }}>LABEL</label>
                <Input
                  value={newRow.label}
                  onChange={(e) => setNewRow({ ...newRow, label: e.target.value })}
                  placeholder="Soundcheck"
                  className="bg-[#1e2220] border-[#2a2e2b] text-sm h-9"
                  data-testid="ros-input-label"
                />
              </div>
              <div className="w-24">
                <label style={{ fontFamily: MONO, fontSize: 10, color: '#666', letterSpacing: 0.5 }}>TIME</label>
                <Input
                  type="time"
                  value={newRow.time}
                  onChange={(e) => setNewRow({ ...newRow, time: e.target.value })}
                  className="bg-[#1e2220] border-[#2a2e2b] text-sm h-9"
                  data-testid="ros-input-time"
                />
              </div>
              <div className="w-20">
                <label style={{ fontFamily: MONO, fontSize: 10, color: '#666', letterSpacing: 0.5 }}>MINS</label>
                <Input
                  type="number"
                  value={newRow.duration_minutes}
                  onChange={(e) => setNewRow({ ...newRow, duration_minutes: e.target.value })}
                  placeholder="30"
                  className="bg-[#1e2220] border-[#2a2e2b] text-sm h-9"
                  data-testid="ros-input-duration"
                />
              </div>
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={saving || !newRow.label || !newRow.time}
                className="h-9"
                style={{ background: AMBER, color: '#0E1110' }}
                data-testid="ros-add-btn"
              >
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 text-sm transition-colors hover:opacity-80"
              style={{ fontFamily: BODY, color: AMBER }}
              data-testid="ros-show-add-btn"
              type="button"
            >
              <Plus size={14} /> Add item
            </button>
          )}
        </>
      )}
    </PanelShell>
  );
}

/* ════════════════════ CREW SLOTS ════════════════════ */

export function CrewSlotsPanel({ bookingId, slots, setSlots, isVenue, summary }) {
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getCrewSlots(bookingId);
      setSlots(res.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, [bookingId, setSlots]);

  useEffect(() => { load(); }, [load]);

  const handleConfirm = async (slotId) => {
    setConfirming(slotId);
    try {
      const res = await confirmCrewSlot(bookingId, slotId);
      setSlots(res.data);
      toast.success('Crew member confirmed');
    } catch { toast.error('Failed to confirm'); }
    finally { setConfirming(null); }
  };

  const handleAddSlot = async () => {
    if (!newRole) return;
    const slot = {
      slot_id: `cs-${Date.now()}`,
      role: newRole,
      assigned_to: newName || null,
      confirmed: false,
    };
    const updated = [...slots, slot];
    setSaving(true);
    try {
      await updateCrewSlots(bookingId, updated);
      setSlots(updated);
      setNewRole('');
      setNewName('');
      toast.success('Crew slot added');
    } catch { toast.error('Failed to add'); }
    finally { setSaving(false); }
  };

  return (
    <PanelShell icon={Users} title="CREW SLOTS" testId="crew-slots-panel" summary={summary}>
      {loading ? (
        <div className="h-16 rounded bg-[#2a2e2b] animate-pulse" />
      ) : (
        <>
          {slots.length === 0 && (
            <p style={{ fontFamily: BODY, fontSize: 13, color: '#666' }} className="mb-3">
              No crew assigned yet.
            </p>
          )}
          <div className="space-y-1 mb-4">
            {/* Header */}
            {slots.length > 0 && (
              <div className="flex items-center gap-3 px-3 py-1.5">
                <span style={{ fontFamily: MONO, fontSize: 12, color: '#a39889', flex: 1, letterSpacing: '0.1em' }}>ROLE</span>
                <span style={{ fontFamily: MONO, fontSize: 12, color: '#a39889', width: 100, letterSpacing: '0.1em' }}>ASSIGNED</span>
                <span style={{ fontFamily: MONO, fontSize: 12, color: '#a39889', width: 80, textAlign: 'center', letterSpacing: '0.1em' }}>STATUS</span>
              </div>
            )}
            {slots.map((s) => (
              <div
                key={s.slot_id}
                data-testid={`crew-slot-${s.slot_id}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                style={{ background: '#1e2220' }}
              >
                <span style={{ fontFamily: BODY, fontSize: 14, color: '#F0EBE0', flex: 1 }}>{s.role}</span>
                <span style={{ fontFamily: BODY, fontSize: 13, color: s.assigned_to ? '#ccc' : '#555', width: 100 }}>
                  {s.assigned_to || '—'}
                </span>
                <div style={{ width: 80, textAlign: 'center' }}>
                  {s.confirmed ? (
                    <span className="inline-flex items-center gap-1">
                      <Check size={14} color={AMBER} />
                      <span style={{ fontFamily: MONO, fontSize: 11, color: AMBER }}>OK</span>
                    </span>
                  ) : (
                    <button
                      onClick={() => handleConfirm(s.slot_id)}
                      disabled={confirming === s.slot_id}
                      className="px-2 py-0.5 rounded text-xs transition-colors hover:bg-[#2a2e2b]"
                      style={{ fontFamily: MONO, fontSize: 11, color: AMBER, border: `1px solid ${AMBER}60`, background: `${AMBER}12` }}
                      data-testid={`crew-confirm-${s.slot_id}`}
                    >
                      {confirming === s.slot_id ? '...' : 'Pending'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {isVenue && (
            showAddForm ? (
              <div className="flex gap-2 items-end" data-testid="crew-add-form">
                <div className="flex-1">
                  <label style={{ fontFamily: MONO, fontSize: 10, color: '#666', letterSpacing: 0.5 }}>ROLE</label>
                  <Input
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    placeholder="Monitor Engineer"
                    className="bg-[#1e2220] border-[#2a2e2b] text-sm h-9"
                    data-testid="crew-input-role"
                  />
                </div>
                <div className="flex-1">
                  <label style={{ fontFamily: MONO, fontSize: 10, color: '#666', letterSpacing: 0.5 }}>NAME</label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Optional"
                    className="bg-[#1e2220] border-[#2a2e2b] text-sm h-9"
                    data-testid="crew-input-name"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleAddSlot}
                  disabled={saving || !newRole}
                  className="h-9"
                  style={{ background: AMBER, color: '#0E1110' }}
                  data-testid="crew-add-btn"
                >
                  <UserPlus size={14} className="mr-1" /> Add
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-1.5 text-sm transition-colors hover:opacity-80"
                style={{ fontFamily: BODY, color: AMBER }}
                data-testid="crew-show-add-btn"
                type="button"
              >
                <Plus size={14} /> Add crew member
              </button>
            )
          )}
        </>
      )}
    </PanelShell>
  );
}

/* ════════════════════ GUEST LIST ════════════════════ */

export function GuestListPanel({ bookingId, guestList, setGuestList, isVenue, summary }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [plusOne, setPlusOne] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getGuestList(bookingId);
      setGuestList(res.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, [bookingId, setGuestList]);

  useEffect(() => { load(); }, [load]);

  const persist = async (updated) => {
    setSaving(true);
    try {
      await updateGuestList(bookingId, updated);
      setGuestList(updated);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleAdd = () => {
    if (!newName.trim()) return;
    const updated = {
      ...guestList,
      entries: [...(guestList.entries || []), { name: newName.trim(), plus_one: plusOne }],
    };
    setNewName('');
    setPlusOne(false);
    persist(updated);
  };

  const handleRemove = (idx) => {
    const entries = [...(guestList.entries || [])];
    entries.splice(idx, 1);
    persist({ ...guestList, entries });
  };

  const handleCapChange = (val) => {
    const cap = parseInt(val) || 0;
    persist({ ...guestList, cap });
  };

  const handleApprove = () => {
    persist({ ...guestList, approved: true });
    toast.success('Guest list approved');
  };

  const entries = guestList?.entries || [];
  const headCount = entries.reduce((n, e) => n + 1 + (e.plus_one ? 1 : 0), 0);
  const cap = guestList?.cap || 0;

  return (
    <PanelShell icon={ListChecks} title="GUEST LIST" testId="guest-list-panel" summary={summary}>
      {loading ? (
        <div className="h-16 rounded bg-[#2a2e2b] animate-pulse" />
      ) : (
        <>
          {/* Cap & count */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span style={{ fontFamily: MONO, fontSize: 12, color: '#a39889', letterSpacing: '0.1em' }}>CAP</span>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={cap || ''}
                onChange={(e) => handleCapChange(e.target.value)}
                className="bg-[#1e2220] border-[#2a2e2b] text-sm h-8 w-20"
                data-testid="guestlist-cap-input"
              />
            </div>
            <div style={{ fontFamily: MONO, fontSize: 13 }}>
              <span style={{ color: headCount > cap && cap > 0 ? '#ef4444' : AMBER }}>{headCount}</span>
              <span style={{ color: '#a39889' }}> / {cap || '—'}</span>
            </div>
            {guestList?.approved && (
              <span
                className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                style={{ background: `${AMBER}18`, border: `1px solid ${AMBER}` }}
              >
                <Check size={12} color={AMBER} />
                <span style={{ fontFamily: MONO, fontSize: 10, color: AMBER }}>APPROVED</span>
              </span>
            )}
          </div>

          {/* Entries */}
          <div className="space-y-1 mb-4">
            {entries.map((e, i) => (
              <div
                key={`${e.name}-${i}`}
                data-testid={`guestlist-entry-${i}`}
                className="flex items-center gap-3 px-3 py-2 rounded-lg"
                style={{ background: '#1e2220' }}
              >
                <span style={{ fontFamily: BODY, fontSize: 14, color: '#F0EBE0', flex: 1 }}>{e.name}</span>
                {e.plus_one && (
                  <span
                    className="px-1.5 py-0.5 rounded text-xs"
                    style={{ fontFamily: MONO, fontSize: 10, color: AMBER, background: `${AMBER}12`, border: `1px solid ${AMBER}40` }}
                  >
                    +1
                  </span>
                )}
                <button
                  onClick={() => handleRemove(i)}
                  className="p-1 rounded hover:bg-red-500/20 transition-colors"
                  data-testid={`guestlist-remove-${i}`}
                >
                  <Trash2 size={14} color="#666" />
                </button>
              </div>
            ))}
            {entries.length === 0 && (
              <p style={{ fontFamily: BODY, fontSize: 13, color: '#666' }}>No guests added yet.</p>
            )}
          </div>

          {/* Add guest */}
          {showAddForm ? (
            <div className="flex gap-2 items-end mb-3" data-testid="guestlist-add-form">
              <div className="flex-1">
                <label style={{ fontFamily: MONO, fontSize: 10, color: '#666', letterSpacing: 0.5 }}>NAME</label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Guest name"
                  className="bg-[#1e2220] border-[#2a2e2b] text-sm h-9"
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  data-testid="guestlist-input-name"
                />
              </div>
              <button
                onClick={() => setPlusOne(!plusOne)}
                className="h-9 px-3 rounded-md text-xs transition-colors"
                style={{
                  fontFamily: MONO,
                  background: plusOne ? `${AMBER}20` : '#1e2220',
                  border: `1px solid ${plusOne ? AMBER : '#2a2e2b'}`,
                  color: plusOne ? AMBER : '#666',
                }}
                data-testid="guestlist-plusone-toggle"
                type="button"
              >
                +1
              </button>
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={saving || !newName.trim()}
                className="h-9"
                style={{ background: AMBER, color: '#0E1110' }}
                data-testid="guestlist-add-btn"
              >
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 text-sm transition-colors hover:opacity-80 mb-3"
              style={{ fontFamily: BODY, color: AMBER }}
              data-testid="guestlist-show-add-btn"
              type="button"
            >
              <Plus size={14} /> Add guest
            </button>
          )}

          {/* Approve button */}
          {isVenue && !guestList?.approved && entries.length > 0 && (
            <Button
              onClick={handleApprove}
              disabled={saving}
              className="w-full"
              style={{ background: `${AMBER}20`, color: AMBER, border: `1px solid ${AMBER}60` }}
              data-testid="guestlist-approve-btn"
            >
              <Check size={14} className="mr-1.5" /> Approve Guest List
            </Button>
          )}
        </>
      )}
    </PanelShell>
  );
}
